import {
  useState,
  useEffect,
  useMemo,
} from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DatePickerField from "@/components/DatePickerField";
import ReceiptUploader, {
  prepareReceiptFile,
} from "@/components/ReceiptUploader";
import * as db from "@/lib/db";

const RECEIPT_STATUS_OPTIONS = [
  "Attached",
  "Missing",
  "Not Required",
];

const PAYMENT_METHODS = [
  "Cash",
  "Bank Transfer",
  "DuitNow / QR",
  "Card",
  "E-Wallet",
  "Other",
];

const PL_TREATMENT_LABEL = {
  operating_expense:
    "Reduces Monthly Net Profit",

  product_purchase_cash_only:
    "Reduces Monthly P&L — Product / Stock Purchase",

  asset_capitalized:
    "Capitalised — automatically added to Asset Register",

  owner_draw:
    "Owner draw — does NOT reduce Net Profit",

  prepaid_event_rental:
    "Cash paid now — excluded from this month's P&L and recognised in the linked Event Rental",

  pending:
    "Pending review — excluded until you assign a treatment",
};

const emptyForm = (defaults = {}) => ({
  id: null,

  txDate: new Date()
    .toISOString()
    .slice(0, 10),

  transactionTypeId: "",
  categoryId: "",
  taxTreatmentId: "",

  supplierCustomer: "",
  description: "",
  amount: "",
  paymentMethod: "",

  receiptNumber: "",
  receiptStatus: "Missing",

  relatedEventId: "",

  businessUsePercentage: "100",

  quantity: "",
  unitCost: "",

  cashFlowDirection: "",

  notes: "",

  ...defaults,
});

const TransactionForm = ({
  open,
  onClose,
  onSaved,
  initial = null,
  types,
  categories,
  taxTreatments,
  events,
  prefillTypeId = null,
}) => {
  const [form, setForm] = useState(
    emptyForm()
  );

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  /*
   * One or multiple receipts can be held here
   * before a new Transaction is created.
   */
  const [
    pendingReceipts,
    setPendingReceipts,
  ] = useState([]);

  useEffect(() => {
    if (!open) return;

    setPendingReceipts([]);

    if (initial) {
      setForm({
        id: initial.id,

        txDate:
          initial.txDate,

        transactionTypeId:
          initial.transactionTypeId,

        categoryId:
          initial.categoryId,

        taxTreatmentId:
          initial.taxTreatmentId ||
          "",

        supplierCustomer:
          initial.supplierCustomer ||
          "",

        description:
          initial.description || "",

        amount:
          initial.amount != null
            ? String(
                initial.amount
              )
            : "",

        paymentMethod:
          initial.paymentMethod ||
          "",

        receiptNumber:
          initial.receiptNumber ||
          "",

        receiptStatus:
          initial.receiptStatus ||
          "Missing",

        relatedEventId:
          initial.relatedEventId ||
          "",

        businessUsePercentage:
          initial.businessUsePercentage !=
          null
            ? String(
                initial.businessUsePercentage
              )
            : "100",

        quantity:
          initial.quantity != null
            ? String(
                initial.quantity
              )
            : "",

        unitCost:
          initial.unitCost != null
            ? String(
                initial.unitCost
              )
            : "",

        cashFlowDirection:
          initial.cashFlowDirection ||
          "",

        notes:
          initial.notes || "",
      });
    } else {
      setForm(
        emptyForm({
          transactionTypeId:
            prefillTypeId || "",
        })
      );
    }
  }, [
    open,
    initial,
    prefillTypeId,
  ]);

  /*
   * ============================================================
   * SELECTED TRANSACTION TYPE
   * ============================================================
   */

  const selectedType =
    useMemo(
      () =>
        types.find(
          (t) =>
            t.id ===
            form.transactionTypeId
        ),
      [
        types,
        form.transactionTypeId,
      ]
    );

  const isProductPurchase =
    selectedType?.plTreatment ===
    "product_purchase_cash_only";

  const isAssetPurchase =
    selectedType?.plTreatment ===
    "asset_capitalized";

  const isPrepaidEventRental =
    selectedType?.plTreatment ===
    "prepaid_event_rental";

  const isPending =
    selectedType?.plTreatment ===
    "pending";

  /*
   * ============================================================
   * CATEGORIES
   * ============================================================
   */

  const filteredCategories =
    useMemo(
      () =>
        categories.filter(
          (c) =>
            c.transactionTypeId ===
            form.transactionTypeId
        ),
      [
        categories,
        form.transactionTypeId,
      ]
    );

  const selectedCategory =
    useMemo(
      () =>
        categories.find(
          (c) =>
            c.id ===
            form.categoryId
        ),
      [
        categories,
        form.categoryId,
      ]
    );

  /*
   * If Transaction Type changes and the existing
   * Category no longer belongs to that Type,
   * clear Category.
   */
  useEffect(() => {
    if (
      form.categoryId &&
      !filteredCategories.some(
        (c) =>
          c.id ===
          form.categoryId
      )
    ) {
      setForm((f) => ({
        ...f,
        categoryId: "",
      }));
    }
  }, [
    form.transactionTypeId,
    filteredCategories,
    form.categoryId,
  ]);

  /*
   * Prepaid Event Rental is always a real
   * cash payment / cash outflow.
   *
   * Keep the form state aligned as well so
   * editing/viewing later also shows the correct
   * cash direction.
   */
  useEffect(() => {
    if (
      isPrepaidEventRental &&
      form.cashFlowDirection !==
        "outflow"
    ) {
      setForm((f) => ({
        ...f,
        cashFlowDirection:
          "outflow",
      }));
    }
  }, [
    isPrepaidEventRental,
    form.cashFlowDirection,
  ]);

  const set = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: v,
    }));

  /*
   * ============================================================
   * VALIDATION
   * ============================================================
   */

  const validate = () => {
    if (!form.txDate) {
      return "Transaction Date is required";
    }

    if (!form.transactionTypeId) {
      return "Transaction Type is required";
    }

    if (!form.categoryId) {
      return "Category is required";
    }

    const amt =
      parseFloat(form.amount);

    if (
      isNaN(amt) ||
      amt <= 0
    ) {
      return "Amount must be greater than zero";
    }

    /*
     * Prepaid Event Rental must always belong
     * to one exact event.
     *
     * Without this link the system would not know
     * which Event P&L should receive the rental.
     */
    if (
      isPrepaidEventRental &&
      !form.relatedEventId
    ) {
      return "Related Event / Activity is required for Prepaid Event Rental";
    }

    const bp =
      parseFloat(
        form.businessUsePercentage
      );

    if (
      isNaN(bp) ||
      bp < 0 ||
      bp > 100
    ) {
      return "Business-use % must be between 0 and 100";
    }

    return null;
  };

  /*
   * ============================================================
   * LINKED ASSET CREATION
   * ============================================================
   */

  const createLinkedAsset =
    async (transaction) => {
      const statuses =
        await db.listAssetStatuses({
          includeArchived: true,
        });

      const activeStatus =
        statuses.find(
          (s) =>
            (s.name || "")
              .toLowerCase()
              .trim() === "active"
        ) ||
        statuses.find(
          (s) =>
            !s.isArchived
        ) ||
        statuses[0];

      const assetName =
        form.description?.trim() ||
        selectedCategory?.name ||
        "Equipment / Asset Purchase";

      const assetPayload = {
        purchaseDate:
          form.txDate,

        name:
          assetName,

        supplier:
          form.supplierCustomer ||
          "",

        category:
          selectedCategory?.name ||
          "",

        purchaseAmount:
          parseFloat(
            form.amount
          ) || 0,

        businessUsePercentage:
          parseFloat(
            form.businessUsePercentage
          ) || 100,

        receiptNumber:
          form.receiptNumber ||
          "",

        assetStatusId:
          activeStatus?.id ||
          "",

        disposalDate:
          "",

        taxCapitalAllowanceStatus:
          "",

        linkedTransactionId:
          transaction.id,

        notes:
          form.notes || "",
      };

      return db.createAsset(
        assetPayload
      );
    };

  /*
   * ============================================================
   * SAVE TRANSACTION
   * ============================================================
   */

  const handleSave = async () => {
    const err =
      validate();

    if (err) {
      toast.error(err);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...form,

        amount:
          parseFloat(
            form.amount
          ) || 0,

        businessUsePercentage:
          parseFloat(
            form.businessUsePercentage
          ) || 100,

        quantity:
          form.quantity === ""
            ? null
            : form.quantity,

        unitCost:
          form.unitCost === ""
            ? null
            : form.unitCost,

        /*
         * Prepaid Event Rental is always
         * a cash outflow.
         */
        cashFlowDirection:
          isPrepaidEventRental
            ? "outflow"
            : form.cashFlowDirection,
      };

      /*
       * ========================================================
       * EDIT EXISTING TRANSACTION
       * ========================================================
       *
       * Existing behaviour remains unchanged.
       *
       * We do not automatically create another
       * Asset when editing an old transaction,
       * because that could create duplicates.
       */

      if (form.id) {
        await db.updateTransaction(
          form.id,
          payload
        );

        toast.success(
          isPrepaidEventRental
            ? "Prepaid Event Rental updated"
            : "Transaction updated"
        );

        onSaved &&
          onSaved();

        onClose &&
          onClose();

        return;
      }

      /*
       * ========================================================
       * NEW TRANSACTION
       * ========================================================
       */

      const preparedReceipts = [];

      /*
       * Prepare every selected receipt BEFORE
       * creating the Transaction.
       *
       * Existing HEIC conversion / compression /
       * validation behaviour remains unchanged.
       */
      for (
        const receipt of pendingReceipts
      ) {
        const prepared =
          await prepareReceiptFile(
            receipt
          );

        preparedReceipts.push(
          prepared
        );
      }

      /*
       * Step 1:
       * Create the Transaction.
       */
      const created =
        await db.createTransaction(
          payload
        );

      /*
       * Step 2:
       * If this is Equipment / Asset Purchase,
       * automatically create and link the Asset.
       */
      if (isAssetPurchase) {
        try {
          await createLinkedAsset(
            created
          );
        } catch (assetError) {
          console.error(
            "Could not create linked asset:",
            assetError
          );

          /*
           * Roll back the new transaction if
           * Asset creation fails.
           */
          try {
            await db.deleteTransaction(
              created.id
            );
          } catch (
            rollbackError
          ) {
            console.error(
              "Transaction rollback failed:",
              rollbackError
            );
          }

          throw new Error(
            assetError?.message ||
              "Could not add this equipment purchase to the Asset Register."
          );
        }
      }

      /*
       * Step 3:
       * Upload ALL selected receipts after
       * Transaction and Asset records have
       * been created.
       */
      let receiptUploadFailed =
        false;

      let uploadedReceiptCount =
        0;

      if (
        preparedReceipts.length >
          0 &&
        created?.id
      ) {
        for (
          const preparedReceipt of
          preparedReceipts
        ) {
          try {
            await db.uploadAttachment({
              entityType:
                "transaction",

              entityId:
                created.id,

              file:
                preparedReceipt.file,

              ext:
                preparedReceipt.ext,

              mime:
                preparedReceipt.mime,
            });

            uploadedReceiptCount += 1;
          } catch (
            receiptError
          ) {
            console.error(
              "Receipt upload failed:",
              receiptError
            );

            receiptUploadFailed =
              true;
          }
        }
      }

      /*
       * ========================================================
       * SUCCESS MESSAGES
       * ========================================================
       */

      const receiptCount =
        pendingReceipts.length;

      /*
       * PREPAID EVENT RENTAL
       */
      if (isPrepaidEventRental) {
        if (
          receiptCount > 0 &&
          !receiptUploadFailed
        ) {
          toast.success(
            receiptCount === 1
              ? "Prepaid Event Rental and receipt saved"
              : `Prepaid Event Rental and ${receiptCount} receipts saved`
          );
        } else if (
          receiptUploadFailed
        ) {
          toast.warning(
            `Prepaid Event Rental saved. ${uploadedReceiptCount} of ${receiptCount} attachment${
              receiptCount === 1
                ? ""
                : "s"
            } uploaded successfully. Any failed attachment can be added later.`
          );
        } else {
          toast.success(
            "Prepaid Event Rental saved and linked to the selected event"
          );
        }
      }

      /*
       * ASSET PURCHASE
       */
      else if (isAssetPurchase) {
        if (
          receiptCount > 0 &&
          !receiptUploadFailed
        ) {
          toast.success(
            receiptCount === 1
              ? "Transaction, receipt and Asset Register entry saved"
              : `Transaction, ${receiptCount} receipts and Asset Register entry saved`
          );
        } else if (
          receiptUploadFailed
        ) {
          toast.warning(
            `Transaction and Asset were saved. ${uploadedReceiptCount} of ${receiptCount} attachment${
              receiptCount === 1
                ? ""
                : "s"
            } uploaded successfully. Any failed attachment can be added later.`
          );
        } else {
          toast.success(
            "Transaction saved and added to Asset Register"
          );
        }
      }

      /*
       * NORMAL TRANSACTION
       */
      else {
        if (
          receiptCount > 0 &&
          !receiptUploadFailed
        ) {
          toast.success(
            receiptCount === 1
              ? "Transaction and receipt saved"
              : `Transaction and ${receiptCount} receipts saved`
          );
        } else if (
          receiptUploadFailed
        ) {
          toast.warning(
            `Transaction saved. ${uploadedReceiptCount} of ${receiptCount} attachment${
              receiptCount === 1
                ? ""
                : "s"
            } uploaded successfully. Any failed attachment can be added later.`
          );
        } else {
          toast.success(
            "Transaction saved"
          );
        }
      }

      onSaved &&
        onSaved();

      onClose &&
        onClose();
    } catch (e) {
      console.error(e);

      toast.error(
        e.message ||
          "Could not save transaction"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const inputClass =
    "bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm";

  const labelClass =
    "text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block";

  const hasPendingReceipts =
    pendingReceipts.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center overflow-y-auto p-0 sm:p-6"
      data-testid="transaction-form-dialog"
    >
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[95vh] overflow-y-auto shadow-xl">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 sm:px-6 py-4 flex items-center justify-between z-10">
          <h2
            className="text-lg font-semibold text-[#193f7d]"
            style={{
              fontFamily:
                "Outfit, sans-serif",
            }}
          >
            {form.id
              ? "Edit Transaction"
              : "Add Transaction"}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
            data-testid="tx-form-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* =====================================================
            BODY
        ====================================================== */}

        <div className="px-5 sm:px-6 py-5 space-y-4">

          {/* DATE + AMOUNT */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Transaction Date *
              </Label>

              <DatePickerField
                value={
                  form.txDate
                }
                onChange={(v) =>
                  set(
                    "txDate",
                    v
                  )
                }
                placeholder="Select date"
                testId="tx-date-picker"
              />
            </div>

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Amount (RM) *
              </Label>

              <Input
                type="number"
                step="0.01"
                min="0"
                value={
                  form.amount
                }
                onChange={(e) =>
                  set(
                    "amount",
                    e.target.value
                  )
                }
                placeholder="0.00"
                className={
                  inputClass
                }
                data-testid="tx-amount-input"
              />
            </div>

          </div>

          {/* TYPE + CATEGORY */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Transaction Type *
              </Label>

              <select
                value={
                  form.transactionTypeId
                }
                onChange={(e) =>
                  set(
                    "transactionTypeId",
                    e.target.value
                  )
                }
                className={`${inputClass} w-full appearance-none cursor-pointer`}
                data-testid="tx-type-select"
              >
                <option value="">
                  Select type…
                </option>

                {types
                  .filter(
                    (t) =>
                      !t.isArchived
                  )
                  .map((t) => (
                    <option
                      key={
                        t.id
                      }
                      value={
                        t.id
                      }
                    >
                      {
                        t.name
                      }
                    </option>
                  ))}
              </select>

              {selectedType && (
                <p
                  className={`text-[11px] mt-1.5 ${
                    isPrepaidEventRental
                      ? "text-blue-700 font-medium"
                      : "text-slate-500"
                  }`}
                  data-testid="tx-type-hint"
                >
                  {
                    PL_TREATMENT_LABEL[
                      selectedType
                        .plTreatment
                    ] ||
                    "No P&L treatment description available"
                  }
                </p>
              )}
            </div>

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Category *
              </Label>

              <select
                value={
                  form.categoryId
                }
                onChange={(e) =>
                  set(
                    "categoryId",
                    e.target.value
                  )
                }
                disabled={
                  !form.transactionTypeId
                }
                className={`${inputClass} w-full appearance-none cursor-pointer disabled:opacity-60`}
                data-testid="tx-category-select"
              >
                <option value="">
                  {form.transactionTypeId
                    ? "Select category…"
                    : "Select type first"}
                </option>

                {filteredCategories
                  .filter(
                    (c) =>
                      !c.isArchived
                  )
                  .map((c) => (
                    <option
                      key={
                        c.id
                      }
                      value={
                        c.id
                      }
                    >
                      {
                        c.name
                      }
                    </option>
                  ))}
              </select>
            </div>

          </div>

          {/* =====================================================
              PREPAID EVENT RENTAL NOTICE
          ====================================================== */}

          {isPrepaidEventRental && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-900 leading-relaxed">
              <strong>
                Prepaid Event Rental:
              </strong>{" "}
              This is a rental payment made before the event. Select the exact event below. The payment is recorded as a cash outflow now but will not reduce this month's Net Profit. It will be included in the linked event's Rental Cost.
              <br />
              <br />
              <strong>
                Important:
              </strong>{" "}
              Do not enter the same prepaid amount again in the event's manual Rental Cost.
            </div>
          )}

          {/* SUPPLIER + PAYMENT METHOD */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Supplier / Customer
              </Label>

              <Input
                value={
                  form.supplierCustomer
                }
                onChange={(e) =>
                  set(
                    "supplierCustomer",
                    e.target.value
                  )
                }
                placeholder={
                  isPrepaidEventRental
                    ? "e.g. Event organiser / venue"
                    : "Optional"
                }
                className={
                  inputClass
                }
                data-testid="tx-supplier-input"
              />
            </div>

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Payment Method
              </Label>

              <select
                value={
                  form.paymentMethod
                }
                onChange={(e) =>
                  set(
                    "paymentMethod",
                    e.target.value
                  )
                }
                className={`${inputClass} w-full appearance-none cursor-pointer`}
                data-testid="tx-payment-method-select"
              >
                <option value="">
                  Select method…
                </option>

                {PAYMENT_METHODS.map(
                  (m) => (
                    <option
                      key={m}
                      value={m}
                    >
                      {m}
                    </option>
                  )
                )}
              </select>
            </div>

          </div>

          {/* DESCRIPTION */}

          <div>
            <Label
              className={
                labelClass
              }
            >
              Description
            </Label>

            <Input
              value={
                form.description
              }
              onChange={(e) =>
                set(
                  "description",
                  e.target.value
                )
              }
              placeholder={
                isAssetPurchase
                  ? "e.g. Chest Freezer, Tea Dispenser, Sealing Machine"
                  : isPrepaidEventRental
                  ? "e.g. October Bazaar booth rental"
                  : "What is this transaction for?"
              }
              className={
                inputClass
              }
              data-testid="tx-description-input"
            />

            {isAssetPurchase && (
              <p className="text-[11px] text-violet-600 mt-1.5">
                This description will become the Asset Name in the Asset Register.
              </p>
            )}
          </div>

          {/* PRODUCT PURCHASE FIELDS */}

          {isProductPurchase && (
            <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4">

              <div>
                <Label
                  className={
                    labelClass
                  }
                >
                  Quantity
                </Label>

                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={
                    form.quantity
                  }
                  onChange={(e) =>
                    set(
                      "quantity",
                      e.target.value
                    )
                  }
                  placeholder="Optional"
                  className={
                    inputClass
                  }
                  data-testid="tx-quantity-input"
                />
              </div>

              <div>
                <Label
                  className={
                    labelClass
                  }
                >
                  Unit Cost (RM)
                </Label>

                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={
                    form.unitCost
                  }
                  onChange={(e) =>
                    set(
                      "unitCost",
                      e.target.value
                    )
                  }
                  placeholder="Optional"
                  className={
                    inputClass
                  }
                  data-testid="tx-unit-cost-input"
                />
              </div>

            </div>
          )}

          {/* RECEIPT NUMBER + STATUS */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Receipt / Invoice No.
              </Label>

              <Input
                value={
                  form.receiptNumber
                }
                onChange={(e) =>
                  set(
                    "receiptNumber",
                    e.target.value
                  )
                }
                placeholder="Optional"
                className={
                  inputClass
                }
                data-testid="tx-receipt-number-input"
              />
            </div>

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Receipt Status
              </Label>

              <select
                value={
                  form.receiptStatus
                }
                onChange={(e) =>
                  set(
                    "receiptStatus",
                    e.target.value
                  )
                }
                className={`${inputClass} w-full appearance-none cursor-pointer`}
                data-testid="tx-receipt-status-select"
              >
                {RECEIPT_STATUS_OPTIONS.map(
                  (s) => (
                    <option
                      key={s}
                      value={s}
                    >
                      {s}
                    </option>
                  )
                )}
              </select>
            </div>

          </div>

          {/* =====================================================
              RELATED EVENT + BUSINESS USE
          ====================================================== */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Related Event / Activity
                {isPrepaidEventRental
                  ? " *"
                  : ""}
              </Label>

              <select
                value={
                  form.relatedEventId
                }
                onChange={(e) =>
                  set(
                    "relatedEventId",
                    e.target.value
                  )
                }
                className={`${inputClass} w-full appearance-none cursor-pointer ${
                  isPrepaidEventRental &&
                  !form.relatedEventId
                    ? "border-blue-300"
                    : ""
                }`}
                data-testid="tx-related-event-select"
              >
                <option value="">
                  {isPrepaidEventRental
                    ? "Select the event receiving this rental…"
                    : "— None —"}
                </option>

                {events.map(
                  (ev) => (
                    <option
                      key={
                        ev.id
                      }
                      value={
                        ev.id
                      }
                    >
                      {
                        ev.startDate
                      }{" "}
                      ·{" "}
                      {
                        ev.eventName
                      }
                    </option>
                  )
                )}
              </select>

              {isPrepaidEventRental && (
                <p className="text-[11px] text-blue-700 mt-1.5">
                  Required. The rental will be recognised in this exact Event P&amp;L.
                </p>
              )}
            </div>

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Business-use %
              </Label>

              <Input
                type="number"
                step="1"
                min="0"
                max="100"
                value={
                  form.businessUsePercentage
                }
                onChange={(e) =>
                  set(
                    "businessUsePercentage",
                    e.target.value
                  )
                }
                className={
                  inputClass
                }
                data-testid="tx-business-use-input"
              />
            </div>

          </div>

          {/* TAX + CASH DIRECTION */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div>
              <Label
                className={
                  labelClass
                }
              >
                Tax Treatment Status
              </Label>

              <select
                value={
                  form.taxTreatmentId
                }
                onChange={(e) =>
                  set(
                    "taxTreatmentId",
                    e.target.value
                  )
                }
                className={`${inputClass} w-full appearance-none cursor-pointer`}
                data-testid="tx-tax-treatment-select"
              >
                <option value="">
                  — None —
                </option>

                {taxTreatments
                  .filter(
                    (t) =>
                      !t.isArchived
                  )
                  .map((t) => (
                    <option
                      key={
                        t.id
                      }
                      value={
                        t.id
                      }
                    >
                      {
                        t.name
                      }
                    </option>
                  ))}
              </select>
            </div>

            {isPending && (
              <div>
                <Label
                  className={
                    labelClass
                  }
                >
                  Cash Direction
                  (optional)
                </Label>

                <select
                  value={
                    form.cashFlowDirection
                  }
                  onChange={(e) =>
                    set(
                      "cashFlowDirection",
                      e.target.value
                    )
                  }
                  className={`${inputClass} w-full appearance-none cursor-pointer`}
                  data-testid="tx-cash-direction-select"
                >
                  <option value="">
                    Exclude from Cash Summary
                  </option>

                  <option value="outflow">
                    Cash Outflow
                  </option>

                  <option value="inflow">
                    Cash Inflow
                  </option>
                </select>
              </div>
            )}

            {isPrepaidEventRental && (
              <div>
                <Label
                  className={
                    labelClass
                  }
                >
                  Cash Direction
                </Label>

                <div
                  className={`${inputClass} flex items-center bg-slate-50 text-slate-700`}
                  data-testid="tx-prepaid-rental-cash-direction"
                >
                  Cash Outflow
                </div>

                <p className="text-[11px] text-slate-500 mt-1.5">
                  Automatically assigned for prepaid rental payments.
                </p>
              </div>
            )}

          </div>

          {/* NOTES */}

          <div>
            <Label
              className={
                labelClass
              }
            >
              Notes
            </Label>

            <Textarea
              value={
                form.notes
              }
              onChange={(e) =>
                set(
                  "notes",
                  e.target.value
                )
              }
              placeholder="Optional"
              rows={3}
              className={`${inputClass} h-auto py-2`}
              data-testid="tx-notes-input"
            />
          </div>

          {/* =====================================================
              RECEIPT ATTACHMENT
          ====================================================== */}

          <div>
            <Label
              className={
                labelClass
              }
            >
              Receipt Attachment
            </Label>

            <ReceiptUploader
              entityType="transaction"
              entityId={
                form.id
              }
              pendingFiles={
                pendingReceipts
              }
              onPendingFilesChange={(
                files
              ) => {
                const nextFiles =
                  Array.isArray(
                    files
                  )
                    ? files
                    : [];

                setPendingReceipts(
                  nextFiles
                );

                if (
                  nextFiles.length >
                    0 &&
                  !form.id
                ) {
                  set(
                    "receiptStatus",
                    "Attached"
                  );
                }

                if (
                  nextFiles.length ===
                    0 &&
                  !form.id &&
                  form.receiptStatus ===
                    "Attached"
                ) {
                  set(
                    "receiptStatus",
                    "Missing"
                  );
                }
              }}
            />
          </div>

          {/* ASSET NOTICE */}

          {isAssetPurchase &&
            !form.id && (
              <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-xs text-violet-800">
                <strong>
                  Asset Register:
                </strong>{" "}
                Saving this transaction will automatically create and link a matching asset in the Asset Register.
              </div>
            )}

          {/* PREPAID RENTAL FINAL NOTICE */}

          {isPrepaidEventRental &&
            form.relatedEventId && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-xs text-emerald-800">
                <strong>
                  Event linked.
                </strong>{" "}
                Once the Event P&amp;L integration is completed, this rental will automatically appear under the selected event's Rental Cost.
              </div>
            )}

        </div>

        {/* =====================================================
            FOOTER
        ====================================================== */}

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 sm:px-6 py-4 flex justify-end gap-3">

          <Button
            variant="outline"
            onClick={onClose}
            disabled={
              submitting
            }
            className="rounded-full"
            data-testid="tx-form-cancel-btn"
          >
            Cancel
          </Button>

          <Button
            onClick={
              handleSave
            }
            disabled={
              submitting
            }
            className="bg-[#193f7d] hover:bg-[#122f5e] text-white rounded-full font-semibold px-6"
            data-testid="tx-form-save-btn"
          >
            {submitting
              ? hasPendingReceipts
                ? "Saving & Uploading…"
                : "Saving…"

              : form.id
              ? "Save Changes"

              : isPrepaidEventRental
              ? hasPendingReceipts
                ? pendingReceipts.length ===
                  1
                  ? "Save Prepaid Rental & Receipt"
                  : `Save Prepaid Rental & ${pendingReceipts.length} Receipts`
                : "Save Prepaid Rental"

              : isAssetPurchase
              ? hasPendingReceipts
                ? pendingReceipts.length ===
                  1
                  ? "Save Transaction, Receipt & Asset"
                  : `Save Transaction, ${pendingReceipts.length} Receipts & Asset`
                : "Save Transaction & Asset"

              : hasPendingReceipts
              ? pendingReceipts.length ===
                1
                ? "Save Transaction & Receipt"
                : `Save Transaction & ${pendingReceipts.length} Receipts`

              : "Save Transaction"}
          </Button>

        </div>

      </div>
    </div>
  );
};

export default TransactionForm;
